import {
  Negotiation,
  NegotiationDetails,
} from '../negotiations/negotiations.model';

export interface Lot {
  _id: string;
  lot_type: string;
  indication: boolean;
  broker_indication: boolean;
  commodity: string;
  product_type: string;
  grade: string;
  specifications: { title: string; value: string }[];
  documentations: { title: string; value: string }[];
  price?: PriceUnion;
  price_units: string;
  currency: string;
  volume_value: number;
  units: string;
  ship_begin: string;
  ship_end: string;
  status: string;
  destination: string;
  delivery_type: string;
  time_frame: string;
  additional_info: string;
  payment_term: string;
  market_type: string;
  origin: string;
  container_type: string;
  governing_rule: string;
  rules_to_govern: string;
  weight_grade_to_govern: string;
  delivery_option: string;
  user_id: string;
  createdAt: Date;
  updatedAt: Date;
  tolerance: string;
}

export interface TableLotData {
  _id: string;
  lot_type: string;
  commodity: string;
  product_type: string;
  grade: string;
  specifications: { title: string; value: string }[];
  documentations: { title: string; value: string }[];
  price?: PriceUnion;
  price_units: string;
  currency: string;
  volume_value: number;
  units: string;
  ship_begin: string;
  ship_end: string;
  status: string;
  destination: string;
  delivery_type: string;
  time_frame: string;
  additional_info: string;
  payment_term: string;
  hasNegotiations: boolean;
  market_type: string;
  origin: string;
  container_type: string;
  governing_rule: string;
  rules_to_govern: string;
  weight_grade_to_govern: string;
  delivery_option: string;
  user_id: string;
  createdAt: Date;
  updatedAt: Date;
  tolerance: string;
}

export interface MyBidsOffersResponse {
  lots: Lot[];
  negotiations: Negotiation[];
}

export interface LotsResponse {
  lots: Lot[];
  negotiations: Negotiation[];
}

export enum MarketType {
  Export = 'Export',
  Feed = 'Feed',
  Track = 'Track',
  Farm = 'Farm',
  Lumber = 'Lumber',
}

export interface LotNegotiationsResponse {
  lot: Lot;
  negotiations: NegotiationDetails[];
  user: User;
}

interface User {
  _id: string;
}

export interface LotDetailsResponse {
  lot: Lot;
  negotiations: Negotiation[];
}

export interface Commodity {
  name: string;
}

export interface NewLotResponse {
  origins: Origin[];
  commodities: CropsGradesCondition[]; // CropsGradesConditions is now commodities (just name change, structure is the same)
  deliveryTypes: DeliveryType[];
  units: Unit[];
}

export interface CropYear {
  _id: string;
  crop_year: number;
  name: string;
}

export interface Unit {
  _id: string;
  name: string;
  name_slug: string;
  name_short: string;
}

export enum ContractTypeEnum {
  Cash = 'Cash',
  Basis = 'Basis',
}

export enum Timeframe {
  Day = 'Day',
  GTC = 'GTC',
  Week = 'Week',
}

export interface ContractType {
  name: string;
}

export interface TimeFrame {
  name: string;
}

export interface GradeCondition {
  grade: string;
  specifications: Specifications[];
  grade_delivery_types: string[];
}

export interface Origin {
  id: string;
  name: string;
}

export interface CropsGradesCondition {
  _id: string;
  commodity: string;
  basis_product_code: string;
  price_types: PriceType[];
  product_types: ProductType[];
  origin: string;
  order: number;
}

export enum PriceType {
  Basis = 'Basis',
  Cash = 'Cash',
}

export interface BasisPrice {
  month?: string;
  year?: number;
  product_code?: string;
  basisPoints?: number;
}

export interface CashPrice {
  amount?: number;
}

export type PriceUnion = CashPrice | BasisPrice;

export interface ProductType {
  product_type: string;
  grades_conditions: GradeCondition[];
}

export interface Specifications {
  title: string;
  order: number;
  defaultValue: string | null;
  isRequired: boolean;
  type: SpecificationType;
}

export enum SpecificationType {
  Number = 'Number',
  Text = 'Text',
  Select = 'Select',
}

export interface Tolerance {
  name: string;
}

export interface Currency {
  name: string;
}

export interface DeliveryType {
  _id: string;
  name: string;
  destinations: string[]; // TODO destinations should change to lcoations and is it's own field in new lot form
  payment_terms: string[];
  container_types: string[];
  delivery_options: string[];
  documentations: Documentation[] | null;
  currencies: string[];
  rules_to_govern: string[];
  packaging_types: string[];
  weight_grade_to_govern: string[];
  tolerances: string[];
  price_units: string[];
  market_type: string;
}

export interface Documentation {
  defaultValue: string | null;
  isRequired: boolean;
  options: string[] | null;
  order: number;
  title: string;
  type: SpecificationType;
}

export interface Units {
  name: string;
  name_short: string;
  name_slug: string;
  _id: string;
}

export interface RulesToGovern {
  name: string;
}
export interface WeightGradeToGovern {
  name: string;
}

export interface PriceUnit {
  name: string;
}

export interface PackagingType {
  packaging: string;
  bag_sizes: string[];
}

export interface DeliveryOption {
  name: string;
}

export interface ContainerType {
  name: string;
}

export interface DestinationType {
  name: string;
}

export interface Destination {
  name: string;
}

export interface PaymentTerm {
  name: string;
}

export function futuresYears(): number[] {
  return Array.from({ length: 4 }, (_, i) => new Date().getFullYear() + i);
}

export const futuresMonths = [
  {
    label: 'January (F)',
    value: 'F',
  },
  {
    label: 'February (G)',
    value: 'G',
  },
  {
    label: 'March (H)',
    value: 'H',
  },
  {
    label: 'April (J)',
    value: 'J',
  },
  {
    label: 'May (K)',
    value: 'K',
  },
  {
    label: 'June (M)',
    value: 'M',
  },
  {
    label: 'July (N)',
    value: 'N',
  },
  {
    label: 'August (Q)',
    value: 'Q',
  },
  {
    label: 'September (U)',
    value: 'U',
  },
  {
    label: 'October (V)',
    value: 'V',
  },
  {
    label: 'November (X)',
    value: 'X',
  },
  {
    label: 'December (Z)',
    value: 'Z',
  },
];
